import type { OutputFormat } from '@/core/project/types';
import { renderSceneToCanvas } from '@/core/render/canvas';
import type { Scene } from '@/core/render/scene';
import { sceneToSvg } from '@/core/render/svg';

/**
 * Получение итогового файла из сцены.
 *
 * Здесь же проверяется фактический MIME результата: canvas.toBlob при
 * неподдерживаемом формате молча возвращает PNG, и отдавать такой файл под
 * расширением .jpg нельзя.
 */

export type ExportedFile = {
  blob: Blob;
  format: OutputFormat;
  mime: string;
  widthPx: number;
  heightPx: number;
  bytes: number;
  /** Расширение без точки. */
  extension: string;
};

export const MIME: Record<OutputFormat, string> = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
};

const EXT: Record<OutputFormat, string> = { png: 'png', jpeg: 'jpg', svg: 'svg' };

/** Предел одной стороны растрового файла. Значение консервативное. */
export const MAX_RASTER_SIDE = 2048;
/** Предел общего числа пикселей: защищает мобильные браузеры от отказа. */
export const MAX_RASTER_PIXELS = 2048 * 2048;

export class ExportError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'ExportError';
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new ExportError('Браузер не смог подготовить файл из canvas.', 'toblob-null'));
          return;
        }
        resolve(blob);
      },
      mime,
      quality,
    );
  });
}

function checkRasterLimits(scene: Scene): void {
  const side = Math.max(scene.width, scene.height);
  if (side > MAX_RASTER_SIDE) {
    throw new ExportError(
      `Сторона изображения ${Math.round(side)} px больше предела ${MAX_RASTER_SIDE} px.`,
      'too-large-side',
    );
  }
  if (scene.width * scene.height > MAX_RASTER_PIXELS) {
    throw new ExportError('Изображение слишком большое по общему числу пикселей.', 'too-many-pixels');
  }
}

export async function exportScene(
  scene: Scene,
  format: OutputFormat,
  options: { jpegQuality?: number } = {},
): Promise<ExportedFile> {
  if (scene.warnings.some((w) => w.level === 'error')) {
    throw new ExportError(
      'Макет содержит ошибки, которые нужно исправить до экспорта.',
      'layout-invalid',
    );
  }

  if (format === 'svg') {
    const svg = sceneToSvg(scene, { xmlDeclaration: true });
    const blob = new Blob([svg], { type: MIME.svg });
    return {
      blob,
      format,
      mime: MIME.svg,
      widthPx: scene.width,
      heightPx: scene.height,
      bytes: blob.size,
      extension: EXT.svg,
    };
  }

  checkRasterLimits(scene);

  // JPEG не умеет прозрачность: подкладываем непрозрачный фон явно.
  const opaque = format === 'jpeg' ? '#FFFFFF' : undefined;
  const canvas = renderSceneToCanvas(scene, opaque ? { opaqueBackground: opaque } : {});
  const quality = format === 'jpeg' ? (options.jpegQuality ?? 0.95) : undefined;
  const blob = await canvasToBlob(canvas, MIME[format], quality);

  if (blob.type !== MIME[format]) {
    throw new ExportError(
      `Браузер вернул файл типа ${blob.type || 'неизвестно'} вместо ${MIME[format]}. Выберите PNG.`,
      'mime-mismatch',
    );
  }

  return {
    blob,
    format,
    mime: blob.type,
    widthPx: canvas.width,
    heightPx: canvas.height,
    bytes: blob.size,
    extension: EXT[format],
  };
}

/**
 * Имя файла не должно раскрывать содержимое QR: ссылка может быть
 * чувствительной, а имя попадёт в список загрузок и в резервные копии.
 */
export function suggestFileName(extension: string, at: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp = `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}-${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `kurlyk-qr-${stamp}.${extension}`;
}
